/**
 * AI ANONYMIZATION SERVICE
 *
 * Pseudonimiza datos de pacientes antes de enviarlos a servicios
 * de IA externos (Gemini, Vertex AI, etc.).
 *
 * Funcionalidades:
 * - Reemplaza nombres reales por pseudónimos consistentes
 * - Mantiene coherencia dentro de una sesión (mismo nombre = mismo pseudo)
 * - Anonimiza emails y teléfonos
 * - Reversible solo en el cliente (mapa de pseudónimos efímero)
 *
 * CUMPLIMIENTO: GDPR Art. 4(5) - Pseudonimización
 */

import { Logger } from '@/core/utils';

const logger = Logger.getLogger('AiAnonymizationService');

// ============================================
// TYPES
// ============================================

export interface AnonymizationMap {
  names: Map<string, string>;       // "María García" → "PERSONA_A"
  emails: Map<string, string>;      // "maria@x.com" → "[EMAIL_1]"
  phones: Map<string, string>;      // "+34612..." → "[TELEFONO_1]"
  patientPseudonym: string;         // Pseudónimo fijo del paciente índice
}

export interface AnonymizationResult {
  anonymizedText: string;
  map: AnonymizationMap;
}

export interface AnonymizedPatientContext {
  patientName?: string;
  familyDescription?: string;
  sessionNotes?: string[];
  genogramMembers?: Array<{ name: string; [key: string]: unknown }>;
  journalEntries?: string[];
  relevantContext?: string;
}

// ============================================
// CONSTANTS
// ============================================

const PERSON_LABELS = [
  'PERSONA_A', 'PERSONA_B', 'PERSONA_C', 'PERSONA_D',
  'PERSONA_E', 'PERSONA_F', 'PERSONA_G', 'PERSONA_H',
  'PERSONA_I', 'PERSONA_J', 'PERSONA_K', 'PERSONA_L',
];

const PATIENT_LABEL = 'PACIENTE';

// Palabras de relación interpersonal (se combinan con posesivos su/mi)
const RELATIONSHIP_WORDS = [
  // Familia nuclear
  'madre', 'padre', 'hermano', 'hermana', 'hijo', 'hija',
  'esposo', 'esposa', 'pareja', 'marido', 'mujer',
  // Familia extendida
  'abuelo', 'abuela', 'nieto', 'nieta',
  'tío', 'tía', 'primo', 'prima', 'sobrino', 'sobrina',
  'cuñado', 'cuñada', 'suegro', 'suegra', 'nuera', 'yerno',
  'madrastra', 'padrastro', 'hijastro', 'hijastra',
  'hermanastro', 'hermanastra',
  'bisabuelo', 'bisabuela',
  // Relaciones sociales
  'amigo', 'amiga', 'novio', 'novia',
  'expareja', 'exmarido', 'exmujer', 'exnovio', 'exnovia',
  'prometido', 'prometida',
  // Relaciones profesionales/sociales
  'jefe', 'jefa', 'compañero', 'compañera', 'colega',
  'vecino', 'vecina', 'conocido', 'conocida',
  'profesor', 'profesora', 'maestro', 'maestra',
  'alumno', 'alumna', 'tutor', 'tutora',
  // Roles terapéuticos
  'terapeuta', 'psicólogo', 'psicóloga', 'psiquiatra',
  'médico', 'médica', 'pediatra',
];

// Generar patrones combinando posesivos con palabras de relación
const POSSESSIVE_PATTERNS = RELATIONSHIP_WORDS.flatMap(rel => [
  `su ${rel}`, `mi ${rel}`,
]);

// Patrones que preceden a un nombre propio (el nombre está DESPUÉS de estas palabras)
const NAME_PRECEDING_PATTERNS = [
  // Títulos y tratamientos
  'paciente', 'dr\\.', 'dra\\.', 'don', 'doña', 'señor', 'señora',
  // Posesivos + relaciones (generados)
  ...POSSESSIVE_PATTERNS,
  // Verbos que introducen nombres
  'me llamo', 'se llama', 'llamado', 'llamada',
  'conocido como', 'conocida como',
  // Acciones con nombres
  'contactar a', 'hablar con', 'referido por', 'referida por',
  'derivado por', 'derivada por', 'recomendado por', 'recomendada por',
  // Contexto terapéutico
  'terapeuta anterior', 'psicólogo anterior', 'psicóloga anterior',
];

// ============================================
// SERVICE
// ============================================

class AiAnonymizationService {

  /**
   * Crea un mapa de anonimización nuevo para una sesión.
   */
  createMap(patientName?: string): AnonymizationMap {
    return {
      names: new Map(patientName ? [[patientName.toLowerCase(), PATIENT_LABEL]] : []),
      emails: new Map(),
      phones: new Map(),
      patientPseudonym: PATIENT_LABEL,
    };
  }

  /**
   * Anonimiza un texto completo reemplazando PII por pseudónimos.
   * Usa un mapa existente o crea uno nuevo.
   * Si no se pasan knownNames, detecta nombres propios automáticamente.
   */
  anonymizeText(
    text: string,
    knownNames: string[],
    patientName?: string,
    existingMap?: AnonymizationMap
  ): AnonymizationResult {
    const map = existingMap || this.createMap(patientName);
    let result = text;

    // 1. Registrar nombres conocidos en el mapa
    if (patientName && !map.names.has(patientName.toLowerCase())) {
      map.names.set(patientName.toLowerCase(), PATIENT_LABEL);
    }

    // 1b. Auto-detectar nombres propios en el texto
    const detectedNames = this.detectNamesInText(text);
    const allNames = [...knownNames, ...detectedNames];

    for (const name of allNames) {
      if (!name || name.toLowerCase() === patientName?.toLowerCase()) continue;
      if (!map.names.has(name.toLowerCase())) {
        const idx = map.names.size - (patientName ? 1 : 0);
        const label = PERSON_LABELS[idx] || `PERSONA_${idx + 1}`;
        map.names.set(name.toLowerCase(), label);
      }
    }

    // 2. Reemplazar nombres (más largos primero para evitar reemplazos parciales)
    // Usa fronteras Unicode (\p{L}) para no reemplazar substrings dentro de palabras
    const sortedNames = [...map.names.entries()].sort((a, b) => b[0].length - a[0].length);
    for (const [name, pseudonym] of sortedNames) {
      const escapedName = this.escapeRegex(name);
      const regex = new RegExp(`(?<![\\p{L}])${escapedName}(?![\\p{L}])`, 'giu');
      result = result.replace(regex, pseudonym);
    }

    // 3. Anonimizar emails
    result = result.replace(
      /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
      (match) => {
        const lower = match.toLowerCase();
        if (!map.emails.has(lower)) {
          map.emails.set(lower, `[EMAIL_${map.emails.size + 1}]`);
        }
        return map.emails.get(lower)!;
      }
    );

    // 4. Anonimizar teléfonos
    result = result.replace(
      /(\+?\d{1,3}[\s-]?)?\(?\d{2,4}\)?[\s.-]?\d{3,4}[\s.-]?\d{3,4}/g,
      (match) => {
        const digits = match.replace(/\D/g, '');
        if (digits.length < 7) return match; // No es un teléfono
        if (!map.phones.has(digits)) {
          map.phones.set(digits, `[TELEFONO_${map.phones.size + 1}]`);
        }
        return map.phones.get(digits)!;
      }
    );

    return { anonymizedText: result, map };
  }

  /**
   * Anonimiza un objeto de contexto de paciente para enviar a IA.
   * Retorna el contexto con todos los nombres reemplazados.
   */
  anonymizePatientContext(
    context: {
      patientName?: string;
      familyDescription?: string;
      sessionNotes?: string[];
      genogramMembers?: Array<{ name: string; [key: string]: unknown }>;
      journalEntries?: string[];
      relevantContext?: string;
    }
  ): { anonymizedContext: AnonymizedPatientContext; map: AnonymizationMap } {
    const map = this.createMap(context.patientName);

    // Recopilar todos los nombres conocidos
    const allNames: string[] = [];
    if (context.patientName) allNames.push(context.patientName);
    if (context.genogramMembers) {
      for (const member of context.genogramMembers) {
        if (member.name) allNames.push(member.name);
      }
    }

    const anonymizedContext: AnonymizedPatientContext = {};

    // Anonimizar descripción familiar
    if (context.familyDescription) {
      const { anonymizedText } = this.anonymizeText(
        context.familyDescription, allNames, context.patientName, map
      );
      anonymizedContext.familyDescription = anonymizedText;
    }

    // Anonimizar notas de sesión
    if (context.sessionNotes) {
      anonymizedContext.sessionNotes = context.sessionNotes.map(note => {
        const { anonymizedText } = this.anonymizeText(note, allNames, context.patientName, map);
        return anonymizedText;
      });
    }

    // Anonimizar miembros del genograma
    if (context.genogramMembers) {
      anonymizedContext.genogramMembers = context.genogramMembers.map(member => {
        const pseudonym = map.names.get(member.name.toLowerCase()) || member.name;
        return { ...member, name: pseudonym };
      });
    }

    // Anonimizar journal
    if (context.journalEntries) {
      anonymizedContext.journalEntries = context.journalEntries.map(entry => {
        const { anonymizedText } = this.anonymizeText(entry, allNames, context.patientName, map);
        return anonymizedText;
      });
    }

    // Anonimizar contexto relevante
    if (context.relevantContext) {
      const { anonymizedText } = this.anonymizeText(
        context.relevantContext, allNames, context.patientName, map
      );
      anonymizedContext.relevantContext = anonymizedText;
    }

    anonymizedContext.patientName = PATIENT_LABEL;

    return { anonymizedContext, map };
  }

  /**
   * Des-anonimiza una respuesta de la IA reemplazando pseudónimos por nombres reales.
   */
  deanonymizeResponse(response: string, map: AnonymizationMap): string {
    let result = response;

    // Revertir nombres (pseudónimo → nombre real)
    for (const [realName, pseudonym] of map.names.entries()) {
      const regex = new RegExp(this.escapeRegex(pseudonym), 'g');
      // Capitalizar cada palabra del nombre (Juan Martínez, no Juan martínez)
      const capitalized = realName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      result = result.replace(regex, capitalized);
    }

    // No revertimos emails/teléfonos en respuestas de IA (no deberían aparecer)

    return result;
  }

  /**
   * Detecta nombres propios en texto en español usando reglas lingüísticas.
   * En español, solo los nombres propios se capitalizan a mitad de frase.
   * Agrupa palabras capitalizadas consecutivas como nombres compuestos.
   */
  private detectNamesInText(text: string): string[] {
    if (!text) return [];
    const detected = new Set<string>();

    // 1. Detectar palabras capitalizadas a mitad de frase (= nombres propios en español)
    // Separar por límites de oración (NO por ":" ya que es inline)
    const sentences = text.split(/(?<=[.!?\n])\s+|(?:^|\n)(?=\S)|(?<=\n)/);

    for (const sentence of sentences) {
      if (!sentence.trim()) continue;
      // Saltar líneas que son encabezados markdown (## Título)
      if (/^#{1,6}\s/.test(sentence.trim())) continue;
      const words = sentence.split(/\s+/);

      // Buscar secuencias de palabras capitalizadas que NO son la primera de la oración
      let i = 0;
      // Saltar la primera palabra (puede estar capitalizada por ser inicio de oración)
      const firstWordEnd = this.findFirstContentWordEnd(words);

      for (i = firstWordEnd; i < words.length; i++) {
        const word = words[i].replace(/[,;:()"""''¿¡!?.…]+/g, '');
        if (!word) continue;

        if (this.isCapitalized(word) && word.length >= 2) {
          // Agrupar palabras capitalizadas consecutivas (Nombre Apellido)
          const nameWords = [word];
          let j = i + 1;
          // Si la palabra original termina en coma/punto y coma, el nombre acaba aquí
          const originalEndsWithSeparator = /[,;]$/.test(words[i]);
          while (j < words.length && !originalEndsWithSeparator) {
            const next = words[j].replace(/[,;:()"""''¿¡!?.…]+/g, '');
            if (this.isCapitalized(next) && next.length >= 2) {
              nameWords.push(next);
              // Si esta palabra también termina en separador, parar después de añadirla
              if (/[,;]$/.test(words[j])) { j++; break; }
              j++;
            } else {
              // Preposiciones dentro de nombres compuestos (ej: "de la Cruz")
              if ((next === 'de' || next === 'del' || next === 'la' || next === 'los' || next === 'las') && j + 1 < words.length) {
                const afterPrep = words[j + 1]?.replace(/[,;:()"""''¿¡!?.…]+/g, '');
                if (afterPrep && this.isCapitalized(afterPrep)) {
                  nameWords.push(next, afterPrep);
                  j += 2;
                  continue;
                }
              }
              break;
            }
          }
          i = j - 1; // Avanzar índice

          const fullName = nameWords.join(' ');
          detected.add(fullName);

          // Si es nombre compuesto, también agregar las partes individuales
          // para capturar menciones posteriores solo con el nombre de pila
          if (nameWords.length >= 2) {
            detected.add(nameWords[0]); // Primer nombre solo
          }
        }
      }
    }

    // 2. Detectar nombres después de patrones indicadores
    const patternRegex = new RegExp(
      `(?:${NAME_PRECEDING_PATTERNS.join('|')})\\s+([A-ZÁÉÍÓÚÑ][a-záéíóúñ]+(?:\\s+[A-ZÁÉÍÓÚÑA-Za-záéíóúñ][a-záéíóúñ]+)*)`,
      'gi'
    );
    let match: RegExpExecArray | null;
    while ((match = patternRegex.exec(text)) !== null) {
      const rawCapture = match[1].trim();
      // El flag 'i' es necesario para patterns, pero sobre-captura el nombre;
      // solo conservar las palabras consecutivas que realmente empiezan con mayúscula
      const words = rawCapture.split(/\s+/);
      const validWords: string[] = [];
      for (const w of words) {
        if (/^[A-ZÁÉÍÓÚÑ][a-záéíóúñ]/.test(w)) {
          validWords.push(w);
        } else {
          break;
        }
      }
      if (validWords.length > 0) {
        const name = validWords.join(' ');
        if (name.length >= 2) {
          detected.add(name);
          if (validWords.length >= 2) detected.add(validWords[0]);
        }
      }
    }

    return [...detected];
  }

  /**
   * Encuentra el índice después de la primera "palabra de contenido" de una oración.
   * Salta prefijos de rol (cualquier Palabra:), marcadores de lista, etc.
   */
  private findFirstContentWordEnd(words: string[]): number {
    if (words.length === 0) return 0;
    // Cualquier palabra seguida de ":" es un prefijo de rol (Usuario:, Familiar:, etc.)
    if (/^\w+:$/i.test(words[0])) return 1;
    // Si es formato "1." o "-" o "*" (marcadores de lista)
    if (/^(\d+[.):]|[-*•])$/.test(words[0])) return 1;
    return 1; // Siempre saltar al menos la primera palabra
  }

  private isCapitalized(word: string): boolean {
    if (!word || word.length < 2) return false;
    // Debe empezar con mayúscula y el resto minúsculas (o mix para acentos)
    return /^[A-ZÁÉÍÓÚÑ][a-záéíóúñ]/.test(word);
  }

  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}

export const aiAnonymizationService = new AiAnonymizationService();
